import type { NavIconName } from './nav-types';

/**
 * The shell's icon set — one 20x20 stroked glyph per nav destination.
 *
 * Inline paths rather than an icon package: the shell needs ~27 glyphs and the
 * smallest maintained icon library costs more gzip than that against the
 * 200 KB initial-route budget. Everything draws in `currentColor`, so a row's
 * hover / active colour carries the icon with it and no module ever needs a
 * descendant selector to re-tint one.
 */
const PATHS: Record<NavIconName, readonly string[]> = {
  dashboard: ['M3.5 3.5h5v5h-5zM11.5 3.5h5v5h-5zM3.5 11.5h5v5h-5zM11.5 11.5h5v5h-5z'],
  students: [
    'M7.25 9.5a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z',
    'M1.75 17v-1.1A3.9 3.9 0 0 1 5.65 12h3.2a3.9 3.9 0 0 1 3.9 3.9V17',
    'M13.4 4.1a2.9 2.9 0 0 1 0 5.3M14.6 12.2A3.5 3.5 0 0 1 18.25 15.7V17',
  ],
  workflow: ['M3.25 3.5h3.5v13h-3.5zM8.25 3.5h3.5v8h-3.5zM13.25 3.5h3.5v10.5h-3.5z'],
  // An inbox tray. It was drawn this way to avoid a second bell beside the one
  // in the sidebar foot; that bell is gone and the row now carries the count
  // itself, but a tray is still the better mark for a place you go to read
  // what arrived, rather than an alert that rings at you.
  notifications: [
    'M2.75 12.25 5.1 5.35a1.5 1.5 0 0 1 1.42-1.02h6.96a1.5 1.5 0 0 1 1.42 1.02l2.35 6.9',
    'M2.75 12.25h3.9l.9 1.85h4.9l.9-1.85h3.9v2.75a1.5 1.5 0 0 1-1.5 1.5H4.25a1.5 1.5 0 0 1-1.5-1.5v-2.75Z',
  ],
  // A list with ticks: the work queue is the things to close today.
  work: [
    'm3 5.75 1.4 1.4 2.35-2.6',
    'M9.75 5.5h7',
    'm3 10.5 1.4 1.4 2.35-2.6',
    'M9.75 10.25h7',
    'm3 15.25 1.4 1.4 2.35-2.6',
    'M9.75 15h7',
  ],
  // A folder: where a student's papers live.
  documents: [
    'M2.75 6.25a1.5 1.5 0 0 1 1.5-1.5h3.6l1.75 1.75h6.15a1.5 1.5 0 0 1 1.5 1.5v7.25a1.5 1.5 0 0 1-1.5 1.5H4.25a1.5 1.5 0 0 1-1.5-1.5V6.25Z',
    'M2.75 9.5h14.5',
  ],
  // A speech bubble: the one place a person writes to a person.
  messages: [
    'M3.25 5.75a1.5 1.5 0 0 1 1.5-1.5h10.5a1.5 1.5 0 0 1 1.5 1.5v6.5a1.5 1.5 0 0 1-1.5 1.5H8.5L4.75 17v-3.25a1.5 1.5 0 0 1-1.5-1.5v-6.5Z',
    'M6.75 7.75h6.5M6.75 10.5h4',
  ],
  batches: [
    'm10 2.5 7.25 3.6L10 9.7 2.75 6.1 10 2.5Z',
    'm2.75 10 7.25 3.6L17.25 10',
    'm2.75 13.9 7.25 3.6 7.25-3.6',
  ],
  attendance: [
    'M4.25 4.75h11.5a1.5 1.5 0 0 1 1.5 1.5v9.25a1.5 1.5 0 0 1-1.5 1.5H4.25a1.5 1.5 0 0 1-1.5-1.5V6.25a1.5 1.5 0 0 1 1.5-1.5Z',
    'M6.5 2.75v3.5M13.5 2.75v3.5M2.75 8.75h14.5',
    'm7 12.6 1.9 1.9 3.6-3.9',
  ],
  sessions: [
    'M4.25 4.75h11.5a1.5 1.5 0 0 1 1.5 1.5v9.25a1.5 1.5 0 0 1-1.5 1.5H4.25a1.5 1.5 0 0 1-1.5-1.5V6.25a1.5 1.5 0 0 1 1.5-1.5Z',
    'M6.5 2.75v3.5M13.5 2.75v3.5M2.75 8.75h14.5',
    'M6.75 11.75h1.5M11.75 11.75h1.5M6.75 14.5h1.5M11.75 14.5h1.5',
  ],
  assignment: [
    'M7.5 4.25H5.25a1.5 1.5 0 0 0-1.5 1.5v10.5a1.5 1.5 0 0 0 1.5 1.5h9.5a1.5 1.5 0 0 0 1.5-1.5V5.75a1.5 1.5 0 0 0-1.5-1.5H12.5',
    'M7.75 2.5h4.5v3.25h-4.5z',
    'M7 10.25h6M7 13.25h4',
  ],
  grading: ['m2.5 10.25 3 3 5.25-5.75', 'm9.25 13.25 1 1 6.75-7.25'],
  results: ['M2.75 17h14.5', 'M5.5 17V9.5M9.25 17V4.75M13 17v-4.5M16.75 17V7.5'],
  tests: [
    'M11.5 2.75H6.25a1.5 1.5 0 0 0-1.5 1.5v11.5a1.5 1.5 0 0 0 1.5 1.5h7.5a1.5 1.5 0 0 0 1.5-1.5V6.5l-3.75-3.75Z',
    'M11.5 2.75V6.5h3.75',
    'M7.5 11h5M7.5 13.75h3',
  ],
  builder: [
    'm3.25 16.75 8-8',
    'm11.75 2.75 1.05 2.45 2.45 1.05-2.45 1.05-1.05 2.45-1.05-2.45L8.25 6.25l2.45-1.05 1.05-2.45Z',
    'm16.25 11.5.75 1.75 1.75.75-1.75.75-.75 1.75-.75-1.75-1.75-.75 1.75-.75.75-1.75Z',
  ],
  // A target, not a document: a practice set is aimed at one weak question
  // type, and it has to read differently from `tests` and `builder` at 20px.
  // A crossed circle: the thing you got wrong, kept where you can find it.
  mistakes: ['M17 10a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z', 'M7.75 7.75l4.5 4.5M12.25 7.75l-4.5 4.5'],
  // Same target glyph as the institute's "Practice sets": the student and the
  // teacher are looking at two ends of one feature, and the icon should say so.
  questionTypes: [
    'M17 10a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z',
    'M13.5 10a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0Z',
    'M10.75 10a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z',
  ],
  practiceSets: [
    'M17 10a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z',
    'M13.5 10a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0Z',
    'M10.75 10a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z',
  ],
  staff: [
    'M4.5 3.5h11a1.5 1.5 0 0 1 1.5 1.5v10a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 3 15V5a1.5 1.5 0 0 1 1.5-1.5Z',
    'M10 9.75a2.1 2.1 0 1 0 0-4.2 2.1 2.1 0 0 0 0 4.2Z',
    'M6.5 14.25a3.5 3.5 0 0 1 7 0',
  ],
  // A banknote: the section is money in and money out, not "charts".
  finance: [
    'M2.75 5.25h14.5a1 1 0 0 1 1 1v7.5a1 1 0 0 1-1 1H2.75a1 1 0 0 1-1-1v-7.5a1 1 0 0 1 1-1Z',
    'M10 12.25a2.25 2.25 0 1 0 0-4.5 2.25 2.25 0 0 0 0 4.5Z',
    'M4.75 7.75h.01M15.25 12.25h.01',
  ],
  // A receipt with a torn edge — what a student is looking for.
  billing: [
    'M5 2.75h10v14.5l-2-1.4-1.65 1.4L10 15.85 8.35 17.25 6.7 15.85l-1.7 1.4V2.75Z',
    'M7.75 6.75h4.5M7.75 9.75h4.5M7.75 12.75h2.5',
  ],
  settings: [
    'M10 12.75a2.75 2.75 0 1 0 0-5.5 2.75 2.75 0 0 0 0 5.5Z',
    'M16.1 12.25a1.25 1.25 0 0 0 .25 1.38l.05.04a1.5 1.5 0 1 1-2.12 2.12l-.05-.05a1.25 1.25 0 0 0-2.12.89V17a1.5 1.5 0 1 1-3 0v-.08a1.25 1.25 0 0 0-2.17-.84l-.05.05a1.5 1.5 0 1 1-2.12-2.12l.05-.05a1.25 1.25 0 0 0-.89-2.12H3.8a1.5 1.5 0 0 1 0-3h.08a1.25 1.25 0 0 0 .84-2.17l-.05-.05A1.5 1.5 0 1 1 6.8 4.5l.05.05a1.25 1.25 0 0 0 2.12-.89V3.5a1.5 1.5 0 1 1 3 0v.08a1.25 1.25 0 0 0 2.12.89l.05-.05a1.5 1.5 0 1 1 2.12 2.12l-.05.05a1.25 1.25 0 0 0 .89 2.12h.14a1.5 1.5 0 1 1 0 3h-.08a1.25 1.25 0 0 0-1.06.79Z',
  ],
  // A person, not a second cog: `settings` already means "configure the
  // institute", and the account screen configures a person.
  account: [
    'M10 10.25a3.25 3.25 0 1 0 0-6.5 3.25 3.25 0 0 0 0 6.5Z',
    'M3.75 17v-.9A4.35 4.35 0 0 1 8.1 11.75h3.8a4.35 4.35 0 0 1 4.35 4.35V17',
  ],
  home: [
    'm2.5 8.75 7.5-6 7.5 6',
    'M4.5 7.75V16.5a.75.75 0 0 0 .75.75h9.5a.75.75 0 0 0 .75-.75V7.75',
    'M8 17.25v-5h4v5',
  ],
  progress: ['m2.75 13.75 4.5-4.5 3 3 6.5-6.5', 'M12.25 5.75h4.5v4.5'],
  practice: [
    'M10 17.5a7.5 7.5 0 1 0 0-15 7.5 7.5 0 0 0 0 15Z',
    'M10 13.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z',
    'M10 11.25a1.25 1.25 0 1 0 0-2.5 1.25 1.25 0 0 0 0 2.5Z',
  ],
  vocab: [
    'M10 5.75v11',
    'M10 5.75A4.25 4.25 0 0 0 5.75 3.5H2.75v11h3.5A4 4 0 0 1 10 16.75',
    'M10 5.75A4.25 4.25 0 0 1 14.25 3.5h3v11h-3.5A4 4 0 0 0 10 16.75',
  ],
  materials: [
    'M2.75 5.75A1.5 1.5 0 0 1 4.25 4.25h3.1l1.65 2h6.75a1.5 1.5 0 0 1 1.5 1.5v7a1.5 1.5 0 0 1-1.5 1.5H4.25a1.5 1.5 0 0 1-1.5-1.5V5.75Z',
  ],
  shadowing: [
    'M10 2.5a2.25 2.25 0 0 1 2.25 2.25v4.5a2.25 2.25 0 0 1-4.5 0v-4.5A2.25 2.25 0 0 1 10 2.5Z',
    'M5.25 9a4.75 4.75 0 0 0 9.5 0',
    'M10 13.75v3.75M7.5 17.5h5',
  ],
  studyPlan: [
    'm7 2.5 1.4 3.1L11.5 7 8.4 8.4 7 11.5 5.6 8.4 2.5 7l3.1-1.4L7 2.5Z',
    'm14 10 .9 2.1 2.1.9-2.1.9-.9 2.1-.9-2.1-2.1-.9 2.1-.9.9-2.1Z',
  ],
  activity: ['M7.5 5.5h9.25M7.5 10h9.25M7.5 14.5h9.25', 'M3.5 5.5h.01M3.5 10h.01M3.5 14.5h.01'],
  tutor: [
    'M17.25 12.25a2 2 0 0 1-2 2H7l-4.25 3V5.25a2 2 0 0 1 2-2h10.5a2 2 0 0 1 2 2v7Z',
    'M6.75 7.5h6.5M6.75 10.5h4',
  ],
  institutes: [
    'M3.5 17.25V4.25a1.5 1.5 0 0 1 1.5-1.5h6a1.5 1.5 0 0 1 1.5 1.5v13',
    'M12.5 8.25h2.75a1.5 1.5 0 0 1 1.5 1.5v7.5',
    'M2.25 17.25h15.5',
    'M6 6h3.5M6 9.5h3.5M6 13h3.5',
  ],
  pending: ['M10 17.5a7.5 7.5 0 1 0 0-15 7.5 7.5 0 0 0 0 15Z', 'M10 5.75v4.5l2.9 1.85'],
  superadmins: [
    'M10 2.5 4.25 4.9v4.35c0 3.55 2.35 6.85 5.75 7.95 3.4-1.1 5.75-4.4 5.75-7.95V4.9L10 2.5Z',
    'm7.5 9.75 1.85 1.85 3.4-3.6',
  ],
  speaking: ['M2.75 8.75v2.5M6.4 6v9M10 3v14M13.6 6v9M17.25 8.75v2.5'],
  // An open envelope: the flap as a chevron over a rounded rectangle. Drawn on
  // the same 20-unit grid and 1.5 stroke as the rest of the set.
  email: ['M3 5.75h14v8.5H3v-8.5Z', 'm3.4 6.2 6.6 4.8 6.6-4.8'],
  // Three dots — the bottom tab bar's "More".
  more: [
    'M4.75 10a1 1 0 1 1 0 .01M10 10a1 1 0 1 1 0 .01M15.25 10a1 1 0 1 1 0 .01',
    'M4.75 10h.01M10 10h.01M15.25 10h.01',
  ],
  band: [
    'M2.75 6.5h3.5M9.75 6.5h7.5M2.75 13.5h8.5M14.75 13.5h2.5',
    'M8 8.5a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z',
    'M13 15.5a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z',
  ],
};

interface NavIconProps {
  name: NavIconName;
  className?: string | undefined;
}

export function NavIcon({ name, className }: NavIconProps): JSX.Element {
  return (
    <svg
      className={className}
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {/* eslint-disable-next-line security/detect-object-injection --
          `name` is the NavIconName union, not user input: every possible value
          is a key literal declared above, so there is no sink to inject into. */}
      {PATHS[name].map((d) => (
        <path key={d} d={d} />
      ))}
    </svg>
  );
}
